import React, { useEffect, useState } from 'react'
import { useFirebaseQuery } from '../hooks/firebaseHooks'
import { useOrganization } from '../context/OrganizationContext'
import { COLLECTIONS, PATHS } from '../utils/shared/constants'
import { DataTable } from '../components/DataTable'
import { useText } from '../context/TextContext'
import { Link } from 'react-router-dom'
import PageHeader from '../components/PageHeader'
import { Dropdown } from 'react-bootstrap'
import { ThreeDotsMenu } from '../components/ThreeDotsMenu'
import { AdvancedDropdownItem } from '../components/AdvancedDropdownItem'
import { useSaveToast } from '../context/SaveToastContext'
import { useFirestore } from 'reactfire'
import { doc, setDoc, where, writeBatch } from 'firebase/firestore'
import { Article } from '../models/shared/Article'
import { calculatePriceWithVat, formatCurrency } from '../utils/shared/priceCalculator'
import { sortArticles } from '../utils/shared/article'
import { toast } from 'react-toastify'

export default function ArticlesPage() {
    const text = useText();
    const { orgId, organization } = useOrganization()
    const currency = organization.settings?.currency ?? 'SEK';
    const [articles] = useFirebaseQuery<Article>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ARTICLES}`, where('status', '==', 'active'));
    const { startSaving, markSaved, stopSaving } = useSaveToast();
    const firestore = useFirestore();
    // Local copy so a reorder shows immediately instead of waiting for the Firestore round trip.
    const [orderedArticles, setOrderedArticles] = useState<Article[]>([]);

    useEffect(() => {
        setOrderedArticles(sortArticles(articles));
    }, [articles]);

    const handleReorder = async (reordered: Article[]) => {
        setOrderedArticles(reordered);
        const saveId = startSaving();

        try {
            const batch = writeBatch(firestore);
            for (let ix = 0; ix < reordered.length; ix++) {
                const article = reordered[ix];
                if (!article.id || article.sortOrder === ix) continue;
                const articleUpdate: Partial<Article> = { sortOrder: ix };
                batch.set(doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ARTICLES}/${article.id}`), articleUpdate, { merge: true });
            }
            await batch.commit();
            markSaved(saveId);
        } catch (e) {
            console.error(e);
            stopSaving();
            toast.error(text.defaultError);
            setOrderedArticles(sortArticles(articles));
        }
    }

    const handleDelete = async (articleId: string) => {
        const saveId = startSaving();

        const articleUpdate: Partial<Article> = {
            status: 'archived'
        }

        await setDoc(doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ARTICLES}/${articleId}`), articleUpdate, { merge: true });

        markSaved(saveId);
    }

    return (
        <>
            <PageHeader title={text.articles}
                description={text.articlesDescription}
                actions={[{ text: text.newArticle, icon: 'bi-plus', link: PATHS.ARTICLES_EDIT }]} />

            <DataTable className='mt-4' data={orderedArticles}
                getRowId={(article) => article.id || ''}
                onReorder={handleReorder}
                columns={[
                    {
                        label: text.name, render: (article) => (
                            <>
                                <Link to={`${PATHS.ARTICLES_EDIT}/${article.id}`}>{article.name}</Link>
                                {article.isActivity && <span className="badge bg-secondary ms-2">{text.activity}</span>}
                            </>
                        )
                    },
                    {
                        label: text.price, render: (article) => {
                            const priceWithVat = calculatePriceWithVat(article.perUnitPrice, article.vatRate);
                            return (
                                <>
                                    {formatCurrency(article.perUnitPrice, currency)}
                                    <div className="text-muted small">
                                        {text.priceWithVat}: {formatCurrency(priceWithVat, currency)}
                                    </div>
                                </>
                            );
                        },
                        align: 'right'
                    },
                    {
                        label: text.vat, render: (article) => `${article.vatRate}%`,
                        align: 'right'
                    },
                    {
                        label: text.actions,
                        align: 'right',
                        render: (article) => {

                            return <ThreeDotsMenu>
                                <AdvancedDropdownItem icon='bi-pencil' text={text.edit} link={`${PATHS.ARTICLES_EDIT}/${article.id}`} />
                                <Dropdown.Divider />
                                <AdvancedDropdownItem onClick={() => handleDelete(article.id || '')} icon='bi-trash' text={text.archive} />
                            </ThreeDotsMenu>
                        }
                    }
                ]}
            />
        </>
    )
}
